import { useEffect, useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { ApiError } from '../../lib/api';
import type { ProjectDetail } from '../../lib/projectsApi';
import type { ProjectMemberRole } from '../../lib/kbProjectsApi';
import { usePeople } from '../../hooks/usePeople';
import { useAddKbProjectMember } from '../../hooks/useKbProject';
import { Select } from '../ui/Select';
import { DateField } from '../ui/DateField';
import { useI18n } from '../../contexts/I18nContext';

// ---------------------------------------------------------------------------
// Manual "add team member" (markdown-first): the picked person is written into
// the project README's `## Echipa` table via POST /api/kb/projects/:code/members
// and the DB rows are re-derived by the sync engine. People already on the team
// (matched by email) are disabled in the picker — re-adding would just update
// their row, which is the edit flow, not this one.
// ---------------------------------------------------------------------------

const ROLES: ProjectMemberRole[] = ['LEAD', 'CONTRIBUTOR', 'REVIEWER', 'STAKEHOLDER'];

export function AddTeamMemberModal({
  project,
  onClose,
  editMember,
}: {
  project: ProjectDetail;
  onClose: () => void;
  /**
   * When set, the modal edits an existing member instead of adding a new one:
   * the person is preselected and locked, the current allocation is preloaded,
   * and re-submitting upserts their `## Echipa` row (CLE-441). Matched by email.
   */
  editMember?: { email: string } | null;
}) {
  const { t } = useI18n();
  const codeOrId = project.code ?? project.id;
  const mutation = useAddKbProjectMember(codeOrId);
  const peopleQuery = usePeople({ active: 'true' });

  const editing = Boolean(editMember);
  const editTarget = useMemo(
    () =>
      editMember
        ? (project.team.members.find(
            (m) => m.email.toLowerCase() === editMember.email.toLowerCase(),
          ) ?? null)
        : null,
    [editMember, project.team.members],
  );

  const [personId, setPersonId] = useState('');
  const [role, setRole] = useState<ProjectMemberRole>('CONTRIBUTOR');
  const [allocation, setAllocation] = useState(() =>
    editTarget ? String(editTarget.allocationHours) : '40',
  );
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  const memberEmails = useMemo(
    () => new Set(project.team.members.map((m) => m.email.toLowerCase())),
    [project.team.members],
  );

  // When editing, resolve the Person id from the loaded roster (members carry
  // Person ids; the add endpoint keys on personId).
  useEffect(() => {
    if (!editing || personId) return;
    const email = editMember?.email.toLowerCase();
    const match = (peopleQuery.data ?? []).find((p) => p.email.toLowerCase() === email);
    if (match) setPersonId(match.id);
  }, [editing, editMember, peopleQuery.data, personId]);

  const personOptions = useMemo(
    () =>
      (peopleQuery.data ?? []).map((p) => ({
        value: p.id,
        label: p.fullName,
        description: p.email,
        // In edit mode only the target person is selectable; otherwise the
        // people already on the team are disabled (re-adding = the edit flow).
        disabled: editing
          ? p.email.toLowerCase() !== editMember?.email.toLowerCase()
          : memberEmails.has(p.email.toLowerCase()),
      })),
    [peopleQuery.data, memberEmails, editing, editMember],
  );

  // Close on Escape; lock background scroll while open.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  function validate(): string | null {
    if (!personId) return t('projects.team.add.errPerson');
    const n = Number(allocation);
    if (allocation.trim() === '' || Number.isNaN(n) || !Number.isInteger(n) || n < 0 || n > 168) {
      return t('projects.team.add.errAllocation');
    }
    if (startDate && endDate && startDate > endDate) {
      return t('projects.team.add.errDates');
    }
    return null;
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    setValidationError(null);
    mutation.mutate(
      {
        personId,
        role,
        allocationHours: Number(allocation),
        startDate: startDate || null,
        endDate: endDate || null,
      },
      { onSuccess: () => onClose() },
    );
  }

  const saving = mutation.isPending;
  const serverError =
    mutation.error instanceof ApiError
      ? mutation.error.message
      : mutation.error
        ? t('projects.team.add.errServer')
        : null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? t('projects.team.edit.title') : t('projects.team.add.title')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
          <h2 className="text-lg font-semibold text-gray-900">
            {editing ? t('projects.team.edit.title') : t('projects.team.add.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-5 py-4">
          {/* Person */}
          <div className="block">
            <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
              {t('projects.team.add.person')} <span className="text-red-500">*</span>
            </span>
            <Select
              label={t('projects.team.add.person')}
              hideLabel
              searchable
              value={personId}
              onChange={setPersonId}
              loading={peopleQuery.isLoading}
              placeholder={t('projects.team.add.personPlaceholder')}
              options={personOptions}
              className="mt-1"
            />
          </div>

          {/* Role + allocation */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('projects.team.add.role')}
              </span>
              <Select
                label={t('projects.team.add.role')}
                hideLabel
                value={role}
                onChange={(v) => setRole(v as ProjectMemberRole)}
                options={ROLES.map((r) => ({ value: r, label: t(`projects.team.role.${r}`) }))}
                className="mt-1"
              />
            </div>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('projects.team.add.allocation')}
              </span>
              <input
                type="number"
                min={0}
                max={168}
                step={1}
                value={allocation}
                onChange={(e) => setAllocation(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </label>
          </div>

          {/* Dates (optional) */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('projects.team.add.startDate')}
              </span>
              <DateField value={startDate} onChange={setStartDate} className="mt-1 w-full" />
            </label>
            <label className="block">
              <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                {t('projects.team.add.endDate')}
              </span>
              <DateField value={endDate} onChange={setEndDate} className="mt-1 w-full" />
            </label>
          </div>

          {(validationError || serverError) && (
            <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">
              {validationError ?? serverError}
            </p>
          )}

          <div className="flex justify-end gap-2 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand-700 disabled:opacity-50"
            >
              {saving && (
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              )}
              {saving
                ? editing
                  ? t('projects.team.edit.saving')
                  : t('projects.team.add.saving')
                : editing
                  ? t('projects.team.edit.submit')
                  : t('projects.team.add.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
